import React from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { requestJson } from '../api/http';

function displayDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit'
  });
}

function relativeActivity(value) {
  if (!value) return '—';
  const elapsed = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(elapsed) || elapsed < 0) return displayDateTime(value);
  if (elapsed < 60000) return 'Just now';
  if (elapsed < 3600000) return `${Math.floor(elapsed / 60000)} min ago`;
  if (elapsed < 86400000) return `${Math.floor(elapsed / 3600000)} hr ago`;
  return displayDateTime(value);
}

function formatDuration(milliseconds) {
  const totalMinutes = Math.max(0, Math.floor(Number(milliseconds || 0) / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  return `${minutes}m`;
}

function isActive(record) {
  return record?.status === 'Online' || record?.status === 'Active';
}

export function TeamMonitorPage() {
  const [historyAccount, setHistoryAccount] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [historyError, setHistoryError] = React.useState('');
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const query = useQuery({
    queryKey: ['team-monitor'],
    queryFn: () => requestJson('/api/users/team-monitor'),
    refetchInterval: 15000,
    refetchIntervalInBackground: true
  });
  const records = query.data?.records || [];

  async function openHistory(account) {
    setHistoryAccount(account);
    setHistory([]);
    setHistoryError('');
    setHistoryLoading(true);
    try {
      const data = await requestJson(`/api/users/team-monitor/history/${encodeURIComponent(account)}`);
      setHistory(data.history || []);
    } catch (error) {
      setHistoryError(error.message || 'Unable to load login history.');
    } finally {
      setHistoryLoading(false);
    }
  }

  return (
    <>
      <div className="page-header reports-page-head">
        <div><h1>Team Monitor</h1><p>Active agents using the shared CSR accounts.</p></div>
        <button type="button" className="btn-secondary" disabled={query.isFetching} onClick={() => query.refetch()}>{query.isFetching ? 'Refreshing…' : '↻ Refresh'}</button>
      </div>
      {query.error ? <div className="empty-state"><p>{query.error.message || 'Unable to load the team monitor.'}</p></div> : null}
      <div className="chart-card reports-table-card">
        <div className="chart-title reports-table-title"><span>CSR Team <small>{records.filter(isActive).length} active</small></span></div>
        <div className="table-wrap reports-table-wrap">
          <table className="reports-table team-monitor-table">
            <thead><tr><th>CSR Account</th><th>Current Agent</th><th>User ID</th><th>Status</th><th>Login Time</th><th>Last Activity</th><th>Time Used</th><th aria-label="Login history">History</th></tr></thead>
            <tbody>
              {records.map(record => (
                <tr key={record.id || record.account}>
                  <td className="td-mono">{record.account}</td>
                  <td>{isActive(record) ? (record.agentAlias || '—') : <span>—<small style={{ display: 'block', opacity: .65 }}>No active session</small></span>}</td>
                  <td className="td-mono">{record.userId || '—'}</td>
                  <td><span className={`report-status ${isActive(record) ? 'report-status-positive' : 'report-status-neutral'}`}>{isActive(record) ? 'Active' : 'Available'}</span></td>
                  <td>{displayDateTime(record.loginTime)}</td>
                  <td title={displayDateTime(record.lastActivity)}>{relativeActivity(record.lastActivity)}</td>
                  <td>{isActive(record) ? formatDuration(now - new Date(record.loginTime || now).getTime()) : '—'}</td>
                  <td><button type="button" className="btn-secondary team-monitor-history-button" style={{ minWidth: 34, padding: '5px 9px', fontSize: 17, lineHeight: 1 }} aria-label={`View login history for ${record.account}`} title="View login history" onClick={() => openHistory(record.account)}>◷</button></td>
                </tr>
              ))}
              {!query.isLoading && !records.length ? <tr><td colSpan="8"><div className="empty-state"><p>No CSR accounts found.</p></div></td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>
      {historyAccount ? (
        <div className="nexus-modal-backdrop" role="presentation" style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'rgba(0,0,0,.72)' }} onMouseDown={(event) => { if (event.target === event.currentTarget) setHistoryAccount(null); }}>
          <div className="nexus-modal-card team-monitor-history-modal" style={{ width: 'min(100%, 1050px)', maxHeight: 'min(80vh, 700px)', overflow: 'auto', background: 'var(--panel, #121212)', border: '1px solid var(--border, #3a3a3a)', borderRadius: 16, padding: 20 }} role="dialog" aria-modal="true" aria-labelledby="team-monitor-history-title">
            <div className="modal-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18 }}>
              <div><h2 id="team-monitor-history-title">Login History</h2><p>{historyAccount}</p></div>
              <button type="button" className="btn-secondary" onClick={() => setHistoryAccount(null)}>Close</button>
            </div>
            {historyLoading ? <div className="empty-state"><p>Loading login history…</p></div> : null}
            {historyError ? <div className="empty-state"><p>{historyError}</p></div> : null}
            {!historyLoading && !historyError ? (
              <div className="table-wrap reports-table-wrap">
                <table className="reports-table">
                  <thead><tr><th>Agent Alias</th><th>User ID</th><th>Status</th><th>Login Time</th><th>Logout Time</th><th>Last Activity</th><th>Time Used</th></tr></thead>
                  <tbody>
                    {history.map(entry => <tr key={entry.id}><td>{entry.agentAlias || '—'}</td><td className="td-mono">{entry.userId || '—'}</td><td>{entry.status || 'Logged Out'}</td><td>{displayDateTime(entry.loginTime)}</td><td>{displayDateTime(entry.logoutTime)}</td><td>{displayDateTime(entry.lastActivity)}</td><td>{isActive(entry) ? formatDuration(now - new Date(entry.loginTime || now).getTime()) : formatDuration(entry.timeUsedMs)}</td></tr>)}
                    {!history.length ? <tr><td colSpan="7"><div className="empty-state"><p>No login history recorded for this ID.</p></div></td></tr> : null}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}

export function TeamMonitorPageMount() {
  const [target, setTarget] = React.useState(null);
  React.useEffect(() => setTarget(document.getElementById('react-team-monitor-root')), []);
  return target ? createPortal(<TeamMonitorPage />, target) : null;
}
